import { yupResolver } from '@hookform/resolvers/yup';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { getProductFromRide, getRouteFromRide } from '../../../helpers/utils/ride';
import { useFormatters } from '../../../hooks';
import { addCredits } from '../../../redux/Users/actions';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '../../../ui/core';
import SelectWithDescription, {
  selectWithDescriptionDefaultValidationSchema,
} from '../../../ui/core/SelectWithDescription';
import { CloseIcon } from '../../../ui/icons';
import { RideStatusChip } from '../Trips/RidersList';
import { RiderCompensationReasons } from '../../../consts';

const MAX_CREDITS_AMOUNT = 600;
const REASON_FOR_CREDITS_OTHER_ID = 'OTHER';

const formDefaultValues = {
  generalCredits: '',
  cancellationCredits: '',
  referralCredits: '',
  selectedReasonForCredits: {
    id: '',
    description: '',
  },
  ride: {
    id: '',
    description: '',
  },
};

// TODO: Create backend endpoint to fetch credit types.
// Keep this synchronous until that endpoint exists. Fetching it through
// react-query made opening the dialog depend on the query resolving, and with
// networkMode 'online' an offline-reporting browser pauses the query forever
// (isLoading true, no error), which silently blocked all credit adjustments.
const CREDIT_TYPES = [
  {
    id: 'generalCredits',
    name: 'General',
  },
  {
    id: 'cancellationCredits',
    name: 'Cancellation',
  },
  {
    id: 'referralCredits',
    name: 'Referral',
  },
];

function ReasonForAddingCredits({ control }) {
  return (
    <SelectWithDescription
      alwaysShowDescription
      control={control}
      focusDescription
      id="reason-for-credits-filter-label-id"
      labelDescription="Reason"
      labelId="reason-for-credits-filter-label"
      name="selectedReasonForCredits"
      options={RiderCompensationReasons}
      otherOptionId={REASON_FOR_CREDITS_OTHER_ID}
      otherOptionLabel="Description"
      placeholder="Select Reason"
      showOtherOption
    />
  );
}

function Ride({ control, error, rideItem, userRideHistory }) {
  const f = useFormatters();
  // Ride history may not have loaded: the fetch failure path is swallowed and
  // never dispatches, and the reducer stores a bare [] when the payload is
  // falsy. Neither shape has a `data` array.
  const recentRides = Array.isArray(userRideHistory?.data) ? userRideHistory.data : [];
  const filteredRideHistory = rideItem ? [rideItem] : recentRides.slice(0, 5);

  const options = filteredRideHistory.map((ride) => {
    const rideRoute = getRouteFromRide(ride);
    const rideProduct = getProductFromRide(ride);
    const formattedDepartureTime = f.date.toLocaleString(ride.departureTime, f.date.DateFormats.DATETIME_MED);
    const formattedFinalAmount = f.number.currency(ride.fare?.finalAmount ?? 0);

    const RideItem = () => {
      return (
        <Box display="flex" flexDirection="column" flex={1} p="2px">
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-start" gap="18px">
              <Typography variant="body2">{formattedDepartureTime}</Typography>
              <RideStatusChip status={ride.status} />
            </Box>
            <Typography fontWeight={600}>{formattedFinalAmount}</Typography>
          </Box>

          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {rideRoute}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {rideProduct}
            </Typography>
          </Box>
        </Box>
      );
    };

    return {
      id: ride.rideId || ride.id,
      Description: RideItem,
      isJsx: true,
    };
  });

  return (
    <SelectWithDescription
      control={control}
      name="ride"
      labelDescription="Ride"
      labelId="ride-filter-label"
      options={options}
      placeholder="Select Ride"
      disabled={rideItem}
    />
  );
}

function checkIfAnyValueHadBeenProvided(valuesAsObject) {
  const allValues = Object.values(valuesAsObject);
  const atLeastOneValueHasBeenProvided = allValues.some((value) => (isNaN(value) ? false : Number(value) !== 0));

  return atLeastOneValueHasBeenProvided;
}

function checkIfAnyValueIsOverpriced(valuesAsObject, maxValue) {
  const allValues = Object.values(valuesAsObject);
  const overPricedValues = allValues.some((value) => Number(value) > maxValue);
  return overPricedValues;
}

function checkCreditAmount(values) {
  const { generalCredits, cancellationCredits, referralCredits } = values || {};
  const atLeastOneCreditHasBeenTyped = checkIfAnyValueHadBeenProvided({
    generalCredits,
    cancellationCredits,
    referralCredits,
  });

  if (!atLeastOneCreditHasBeenTyped) {
    return this.createError({ path: 'creditGroup', message: 'At least one credit amount must be provided' });
  }

  const overPricedValues = checkIfAnyValueIsOverpriced(
    {
      generalCredits,
      cancellationCredits,
      referralCredits,
    },
    MAX_CREDITS_AMOUNT,
  );

  if (overPricedValues) {
    return this.createError({
      path: 'creditGroup',
      message: `Amount must not be higher than $${MAX_CREDITS_AMOUNT}`,
    });
  }

  return true;
}

function ManageCreditsDialog({ isOpen, onClose, onCreditsUpdated, userData, rideItem, userRideHistory }) {
  const [submitError, setSubmitError] = useState(null);
  const dispatch = useDispatch();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: structuredClone(formDefaultValues),
    resolver: yupResolver(
      yup
        .object()
        .shape({
          GENERAL: yup.number().typeError('Must be a valid number'),
          CANCELLATION: yup.number().typeError('Must be a valid number'),
          REFERRAL: yup.number().typeError('Must be a valid number'),
          selectedReasonForCredits: yup
            .object()
            .shape({
              id: yup.string().required('Reason is required'),
              description: yup.string().required('Description is required'),
            })
            .typeError('Reason is required'),
          ride: selectWithDescriptionDefaultValidationSchema({
            entity: 'Ride',
            required: false,
          }),
        })
        .test(
          'at-least-one-credit-amount-must-be-provided',
          'At least one credit amount must be provided',
          checkCreditAmount,
        ),
    ),
  });

  const currentFormValues = useWatch({ control: form.control });
  const { isSubmitting } = useFormState({ control: form.control });

  // This effect hides error messages after 3 seconds
  useEffect(() => {
    if (submitError) {
      setTimeout(() => {
        setSubmitError(null);
      }, 3_000);
    }
  }, [submitError]);

  function handleClose(values) {
    onClose(values ?? null);

    // This delay improves the UX by preventing users from
    // seeing the form values being reset to 0.00 while the
    // dialog is being closed.
    setTimeout(() => {
      if (form) {
        form.reset(formDefaultValues);
      }
    }, 500);
  }

  async function onSubmit(dataToSubmit) {
    const { cancellationCredits, generalCredits, referralCredits, selectedReasonForCredits, ride } = dataToSubmit;

    const auditEntriesForBackend = [
      {
        type: 'CANCELLATION',
        key: 'cancellationCredits',
        value: Number(cancellationCredits),
        updatedValue: creditAmountValuesAfterUpdate.cancellationCredits,
      },
      {
        type: 'GENERAL',
        key: 'generalCredits',
        value: Number(generalCredits),
        updatedValue: creditAmountValuesAfterUpdate.generalCredits,
      },
      {
        type: 'REFERRAL',
        key: 'referralCredits',
        value: Number(referralCredits),
        updatedValue: creditAmountValuesAfterUpdate.referralCredits,
      },
    ].filter((entry) => entry.value !== 0);

    try {
      const bodyPayload = {
        userId: userData.id,
        reason: selectedReasonForCredits.id,
        description: selectedReasonForCredits.description,
        items: auditEntriesForBackend,
        rideId: ride?.id || null,
      };

      const result = await addCredits(bodyPayload)(dispatch);

      if (result.success) {
        if (onCreditsUpdated) {
          await onCreditsUpdated();
        }

        handleClose({
          success: result.success,
        });

        await new Promise((resolve) => setTimeout(resolve, 1_000));
      } else {
        throw new Error(result.error?.message);
      }
    } catch (error) {
      setSubmitError(`Could not update credits. Please try again later.`);
      console.error(error);
    }
  }

  const initialValuesForInputs = useMemo(() => {
    return {
      generalCredits: (userData.creditAmount || 0).toFixed(2),
      cancellationCredits: (userData.cancellationCredit || 0).toFixed(2),
      referralCredits: (userData.referralCredit || 0).toFixed(2),
    };
  }, [userData]);

  const creditAmountValuesAfterUpdate = {
    // "|| 0" prevents "NaN" values from being displayed
    generalCredits: Number(initialValuesForInputs.generalCredits) + (Number(currentFormValues.generalCredits) || 0),
    cancellationCredits:
      Number(initialValuesForInputs.cancellationCredits) + (Number(currentFormValues.cancellationCredits) || 0),
    referralCredits: Number(initialValuesForInputs.referralCredits) + (Number(currentFormValues.referralCredits) || 0),
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{ userSelect: 'none' }}
      component="form"
      onSubmit={form.handleSubmit((dataToSubmit) => onSubmit(dataToSubmit))}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Manage Credits</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Box sx={{ marginBottom: '32px' }}>
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto', width: '100%', minWidth: 500 }}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'spacer.main' }}>
                  <TableCell style={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Current</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Change ±</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>New</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {CREDIT_TYPES.map((creditType) => {
                  return (
                    <TableRow key={creditType.id}>
                      <TableCell component="th" scope="row">
                        {creditType.name}
                      </TableCell>
                      <TableCell>${initialValuesForInputs[creditType.id]}</TableCell>
                      <TableCell>
                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ position: 'absolute', left: '4px', opacity: 0.7, userSelect: 'none' }}>
                              $
                            </span>
                            <TextField
                              value={currentFormValues[creditType.id]}
                              onChange={(e) => {
                                const updatedValue = e.target.value;
                                const centsLength = updatedValue.split('.')[1]?.length || 0;
                                const numberValue = Number(updatedValue) || 0;
                                if (centsLength > 2 || numberValue > MAX_CREDITS_AMOUNT) {
                                  return;
                                }
                                form.setValue(creditType.id, numberValue);
                              }}
                              inputProps={{
                                style: {
                                  textAlign: 'right',
                                  padding: '6px',
                                  width: '100px',
                                  marginLeft: '-6px',
                                },
                                type: 'number',
                                inputMode: 'decimal',
                                step: 0.01,
                                max: MAX_CREDITS_AMOUNT,
                              }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ width: '100px' }}>
                        ${creditAmountValuesAfterUpdate[creditType.id].toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <FormHelperText error>{form.formState.errors.creditGroup?.message}</FormHelperText>
        </Box>

        <Box>
          <ReasonForAddingCredits control={form.control} />
        </Box>

        <Box sx={{ marginTop: '32px' }}>
          <Ride
            control={form.control}
            userId={userData.id}
            rideItem={rideItem}
            userRideHistory={userRideHistory}
            error={form.formState.errors.ride}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Box sx={{ width: '100%', height: '52px' }}>
          {submitError ? (
            <Alert severity="error" sx={{ height: '100%', width: '100%' }}>
              {submitError}
            </Alert>
          ) : (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              color="primary"
              sx={{ height: '100%' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ManageCreditsDialog;
